import { ICON__chevron_down } from '@/assets/icons_fa';
import { UI_PRIMITIVE__button } from '@/components/ui/UI_PRIMITIVE__button';
import { UI_PRIMITIVE__dropdown_menu } from '@/components/ui/dropdown/UI_PRIMITIVE__dropdown_menu';
import { UI_PRIMITIVE__dropdown_menu_content } from '@/components/ui/dropdown/UI_PRIMITIVE__dropdown_menu_content';
import { UI_PRIMITIVE__dropdown_menu_item } from '@/components/ui/dropdown/UI_PRIMITIVE__dropdown_menu_item';
import { UI_PRIMITIVE__dropdown_menu_trigger } from '@/components/ui/dropdown/UI_PRIMITIVE__dropdown_menu_trigger';
import NotifierBorder from '@/components/ui/notifier/notifier-border';
import { ENUM__component_type } from '@/enums/ENUM__component_type';
import { HOOK__notifier } from '@/hooks/HOOK__notifier';
import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_slice_search, use_slice_workspace } from '@/hooks/store/use_slices';
import i18n_page_search from '@/locales/keys/i18n_page_search.json';
import { PLACEHOLDER_bar_line } from '@/pages/_base/views/PLACEHOLDER_bar_line';
import { ENUM__i18n_namespace } from '@/services/i18n/ENUM__i18n_namespace';
import { remote_search_update } from '@/state/search/remote_search_update';
import { ENUM__workspace_entity } from '@shared/dto/+workspace/workspace_entity/_enums/ENUM__workspace_entity';
import { ENUM__workspace_entity_feature } from '@shared/dto/+workspace/workspace_entity/_enums/ENUM__workspace_entity_feature';
import { ESearchImageOrientation } from '@shared/dto/search/ESearchImageOrientation';
import { useTranslation } from 'react-i18next';

export function SOrientationDropdown() {
  const dispatch = use_dispatch();

  const { t } = useTranslation(ENUM__i18n_namespace.PAGE_SEARCH);

  const { search_current } = use_slice_search(['search_current']);
  const { current_workspace_user } = use_slice_workspace(['current_workspace_user']);

  const component_id = `${ENUM__component_type.PAGE_SEARCH_ORIENTATION_DROPDOWN_TRIGGER}_${search_current._id}`;
  const notifier = HOOK__notifier(component_id, ENUM__workspace_entity.ENTITY_SEARCH);

  const trigger_id = 'dropdown_search_orientation';

  /*
   * EVENTS
   */

  async function on_item_selected(value: string) {
    if (search_current.orientations.includes(value)) return;

    let orientations_new = value == ESearchImageOrientation.ALL ? [] : [value];

    let search_new = search_current.clone({ orientations: orientations_new });

    await dispatch(remote_search_update(search_new));

    await notifier.send_action();
  }

  /*
   * METHODS
   */

  function is_disabled() {
    return !current_workspace_user.is_feature_pertmitted(ENUM__workspace_entity_feature.ENTITY_SEARCH_UPDATE);
  }

  function user_can_view() {
    if (search_current.is_stub()) return false;

    return search_current.is_shared || current_workspace_user.is_feature_pertmitted(ENUM__workspace_entity_feature.ENTITY_SEARCH_VIEW);
  }

  /*
   * RENDERS
   */

  function render_items() {
    return Object.values(ESearchImageOrientation).map((value: string) => {
      return (
        <UI_PRIMITIVE__dropdown_menu_item
          key={value.toString()}
          values_selected={search_current.orientations}
          class_name_hovered_selected="bg-accent"
          {...{ on_item_selected, value }}
          className={`w-full rounded-lg transition-colors duration-300 cursor-pointer text-left flex justify-between
         `}
        >
          <span className="w-20">{t(i18n_page_search.sidebar.dropdown.orientation.items[value])}</span>
        </UI_PRIMITIVE__dropdown_menu_item>
      );
    });
  }

  /*
   * HTML
   */

  if (!user_can_view()) return <PLACEHOLDER_bar_line />;

  return (
    <>
      <UI_PRIMITIVE__dropdown_menu>
        <UI_PRIMITIVE__dropdown_menu_trigger asChild>
          <UI_PRIMITIVE__button id={trigger_id} className="relative w-full justify-start h-9" variant="outline-on-bar" disabled={is_disabled()}>
            <NotifierBorder notifier={notifier} />
            <span className="w-full text-left">{t(i18n_page_search.sidebar.dropdown.orientation.title)}:</span>
            <span className="max-w-32 min-w-28 text-right">
              {search_current.orientations.is_empty()
                ? t(i18n_page_search.sidebar.dropdown.orientation.items[ESearchImageOrientation.ALL])
                : t(i18n_page_search.sidebar.dropdown.orientation.items[search_current.orientations[0]])}
            </span>
            <ICON__chevron_down className="!w-5 !h-5 !text-component-item-foreground" />
          </UI_PRIMITIVE__button>
        </UI_PRIMITIVE__dropdown_menu_trigger>
        <UI_PRIMITIVE__dropdown_menu_content trigger_id={trigger_id} className=" rounded-lg">
          {render_items()}
        </UI_PRIMITIVE__dropdown_menu_content>
      </UI_PRIMITIVE__dropdown_menu>
    </>
  );
}
